import { useCallback, useRef, useState } from 'react';

/**
 * Tracks whether the attached element is currently at least `threshold` visible, updating on every
 * change. Use `useInViewOnce` instead for analytics impressions, which latch on first view.
 *
 * Returns `[inView, ref]`. `ref` is a callback ref so it fires when the element mounts behind a
 * loading state. Reports `true` when IntersectionObserver is unavailable, so callers gating UI on
 * it fail open.
 */
export function useIsInView<T extends HTMLElement>(threshold = 0): [boolean, (element: T | null) => void] {
  const [inView, setInView] = useState(false);
  const observer = useRef<IntersectionObserver | null>(null);

  const ref = useCallback(
    (element: T | null) => {
      observer.current?.disconnect();
      observer.current = null;

      if (!element) {
        return;
      }

      if (typeof IntersectionObserver === 'undefined') {
        setInView(true);
        return;
      }

      observer.current = new IntersectionObserver(
        (entries) => {
          setInView(entries.some((entry) => entry.isIntersecting));
        },
        { threshold },
      );

      observer.current.observe(element);
    },
    [threshold],
  );

  return [inView, ref];
}
